/**
 * Data-driven definitions for AdminDashboard tile grids.
 *
 * Each array corresponds to a section of the dashboard.  New editors/tiles
 * should be added here rather than duplicated as JSX.
 *
 * NOTE: Because Tailwind's JIT scans source files for full class strings,
 * gradient/border classes must be complete literal strings – do not compose
 * them from variables here.
 */
import {
  Music, Package, Users, Video, Calendar, TrendingUp, UserCog, Drum,
  Image, Layout, DollarSign, Mail, Settings, Headphones, Edit3, FileText,
  GraduationCap, Crown, Tv, Layers, Palette, Wand2, Radio, VideoIcon,
  Images, BookOpen, BarChart3, QrCode, Wrench, CloudDownload, CreditCard,
} from 'lucide-react';

// ---------- Quick Actions (plain cards + a few "primary" highlighted) ----------
export const quickActionsTiles = [
  {
    to: '/admin/homepage',
    icon: Settings,
    iconColor: 'text-primary',
    title: 'Homepage Editor',
    description: 'Edit hero copy, featured video, 3-column blurbs, Why-Choose-Us list and testimonials',
    variant: 'primary',
    testId: 'homepage-editor-btn',
    delay: 0.37,
  },
  {
    to: '/admin/hero-slides',
    icon: Images,
    iconColor: 'text-primary',
    title: 'Home Slideshow',
    description: 'Edit the 12 homepage photos and their captions',
    variant: 'primary',
    testId: 'home-slideshow-btn',
    delay: 0.38,
  },
  {
    to: '/admin/hero-editor',
    icon: Layout,
    iconColor: 'text-primary',
    title: 'Hero Section Editor',
    description: 'Edit the hero section on every page from one place',
    variant: 'primary',
    testId: 'hero-editor-btn',
    delay: 0.385,
  },
  {
    to: '/admin/gallery-creator',
    icon: Images,
    iconColor: 'text-primary',
    title: 'Gallery Creator',
    description: 'Build gMedia-style galleries with 19 themes, albums, and lightbox',
    variant: 'primary',
    testId: 'gallery-creator-btn',
    delay: 0.39,
  },
  {
    to: '/admin/performances',
    icon: Video,
    iconColor: 'text-primary',
    title: 'Performance Details',
    description: 'Edit titles, videos, images and details for all 17 performances',
    variant: 'primary',
    testId: 'performances-editor-btn',
    delay: 0.39,
  },
  {
    to: '/admin/music',
    icon: Music,
    iconColor: 'text-primary',
    title: 'Manage Music',
    description: 'Add, edit, or delete tracks',
    delay: 0.4,
  },
  {
    to: '/admin/track-repair',
    icon: Wrench,
    iconColor: 'text-amber-400',
    title: 'Track Repair',
    description: 'Scan every song — find which ones deliver only a 30s sample',
    testId: 'track-repair-tile',
    delay: 0.41,
  },
  {
    to: '/admin/payment-settings',
    icon: CreditCard,
    iconColor: 'text-emerald-400',
    title: 'Payment Settings',
    description: 'Live Stripe key, test the live payment flow',
    testId: 'payment-settings-tile',
    delay: 0.42,
  },
  {
    to: '/admin/content-sync',
    icon: CloudDownload,
    iconColor: 'text-sky-400',
    title: 'Content Sync',
    description: 'Pull latest preview content into the live site',
    testId: 'content-sync-tile',
    delay: 0.43,
  },
  {
    to: '/admin/videos',
    icon: Video,
    iconColor: 'text-purple-500',
    title: 'Manage Videos',
    description: 'Add video courses',
    delay: 0.45,
  },
  {
    to: '/admin/classes',
    icon: Calendar,
    iconColor: 'text-green-500',
    title: 'Manage Classes',
    description: 'Classes & registrations',
    delay: 0.47,
  },
  {
    to: '/admin/orders',
    icon: Package,
    iconColor: 'text-secondary',
    title: 'View Orders',
    description: 'See all purchases',
    delay: 0.5,
  },
  {
    to: '/admin/customers',
    icon: Users,
    iconColor: 'text-accent',
    title: 'Customers',
    description: 'View customer list',
    delay: 0.6,
  },
  {
    to: '/admin/settings',
    icon: TrendingUp,
    iconColor: 'text-primary',
    title: 'Site Images',
    description: 'Upload hero images',
    delay: 0.7,
  },
  {
    to: '/admin/users',
    icon: UserCog,
    iconColor: 'text-blue-500',
    title: 'Manage Users',
    description: 'View and manage user accounts',
    testId: 'manage-users-btn',
    delay: 0.75,
  },
  {
    to: '/admin/play-along-editor',
    icon: Drum,
    iconColor: 'text-orange-500',
    title: 'Play Along Editor',
    description: 'Manage Play Along videos & instruments',
    testId: 'play-along-editor-btn',
    delay: 0.8,
  },
];

// ---------- Live Lessons + Gallery + Kapoeira (compact gradient tiles) ----------
export const liveGalleryTiles = [
  {
    to: '/admin/live-lessons',
    icon: VideoIcon,
    iconColor: 'text-cyan-400',
    title: 'Live Lessons Admin',
    description: 'Schedule lessons, go live with students, manage bookings',
    variant: 'wide',
    gradientClass: 'bg-gradient-to-br from-cyan-500/30 to-blue-500/30',
    borderClass: 'border-2 border-cyan-500/50 hover:border-cyan-500',
    iconBgClass: 'bg-gradient-to-br from-cyan-500 to-blue-500',
    testId: 'live-lessons-admin-btn',
    delay: 0.55,
  },
  {
    to: '/admin/gallery',
    icon: Images,
    iconColor: 'text-pink-400',
    title: 'Photo Gallery Editor',
    description: 'Manage 40 years of Escola de Samba Casa Samba history albums & photos',
    variant: 'wide',
    gradientClass: 'bg-gradient-to-br from-pink-500/30 to-purple-500/30',
    borderClass: 'border-2 border-pink-500/50 hover:border-pink-500',
    iconBgClass: 'bg-gradient-to-br from-pink-500 to-purple-500',
    testId: 'gallery-admin-btn',
    delay: 0.57,
  },
  {
    to: '/admin/kapoeira-editor',
    icon: BookOpen,
    iconColor: 'text-amber-400',
    title: 'Kapoeira Editor',
    description: 'Manage Capoeira Angola page & Kapoeira Book content',
    variant: 'wide',
    gradientClass: 'bg-gradient-to-br from-amber-500/30 to-yellow-500/30',
    borderClass: 'border-2 border-amber-500/50 hover:border-amber-500',
    iconBgClass: 'bg-gradient-to-br from-amber-500 to-yellow-500',
    testId: 'kapoeira-editor-btn',
    delay: 0.59,
  },
];

// ---------- Media Library + Editors row (mixed gradient + plain) ----------
export const mediaAndEditorsTiles = [
  {
    to: '/admin/media',
    icon: Image,
    iconColor: 'text-blue-500',
    title: 'Media Library',
    description: 'Upload and manage images, audio files',
    delay: 0.75,
  },
  {
    to: '/admin/site-editor',
    icon: Layout,
    iconColor: 'text-purple-400',
    title: 'Live Site Editor',
    description: 'Edit your existing pages live - Home, About, Videos, Courses, PPV & more',
    variant: 'wide',
    gradientClass: 'bg-gradient-to-br from-purple-500/30 to-pink-500/30',
    borderClass: 'border-2 border-purple-500/50 hover:border-purple-500',
    iconBgClass: 'bg-gradient-to-br from-purple-500 to-pink-500',
    colSpanClass: 'md:col-span-2',
    testId: 'live-site-editor-btn',
    delay: 0.75,
  },
  {
    to: '/admin/about-editor',
    icon: DollarSign,
    iconColor: 'text-green-400',
    title: 'Funder Pitch / Fundraising Editor',
    description: 'Edits the Ancestral String Instruments pitch modal on /legacy · Funder PDF summary',
    variant: 'wide',
    gradientClass: 'bg-gradient-to-br from-green-500/30 to-teal-500/30',
    borderClass: 'border-2 border-green-500/50 hover:border-green-500',
    iconBgClass: 'bg-gradient-to-br from-green-500 to-teal-500',
    colSpanClass: 'md:col-span-2',
    testId: 'funder-pitch-editor-btn',
    delay: 0.77,
  },
  {
    to: '/admin/contact-messages',
    icon: Mail,
    iconColor: 'text-blue-400',
    title: 'Contact Messages',
    description: 'View and manage messages from the contact form',
    variant: 'wide',
    gradientClass: 'bg-gradient-to-br from-blue-500/30 to-cyan-500/30',
    borderClass: 'border-2 border-blue-500/50 hover:border-blue-500',
    iconBgClass: 'bg-gradient-to-br from-blue-500 to-cyan-500',
    colSpanClass: 'md:col-span-2',
    testId: 'contact-messages-btn',
    delay: 0.78,
  },
  {
    to: '/admin/quote-requests',
    icon: Calendar,
    iconColor: 'text-orange-400',
    title: 'Quote Requests',
    description: 'Manage booking inquiries and quote requests from customers',
    variant: 'wide',
    gradientClass: 'bg-gradient-to-br from-orange-500/30 to-red-500/30',
    borderClass: 'border-2 border-orange-500/50 hover:border-orange-500',
    iconBgClass: 'bg-gradient-to-br from-orange-500 to-red-500',
    colSpanClass: 'md:col-span-2',
    testId: 'quote-requests-btn',
    delay: 0.785,
  },
  {
    to: '/admin/quote-form-editor',
    icon: Settings,
    iconColor: 'text-pink-400',
    title: 'Quote Form Editor',
    description: 'Customize the options in your quote request form',
    variant: 'wide',
    gradientClass: 'bg-gradient-to-br from-pink-500/30 to-rose-500/30',
    borderClass: 'border-2 border-pink-500/50 hover:border-pink-500',
    iconBgClass: 'bg-gradient-to-br from-pink-500 to-rose-500',
    colSpanClass: 'md:col-span-2',
    testId: 'quote-form-editor-btn',
    delay: 0.79,
  },
  {
    to: '/admin/pricing-editor',
    icon: DollarSign,
    iconColor: 'text-green-400',
    title: 'Pricing Editor',
    description: 'Set personnel rates, packages, and pricing options',
    variant: 'wide',
    gradientClass: 'bg-gradient-to-br from-green-500/30 to-emerald-500/30',
    borderClass: 'border-2 border-green-500/50 hover:border-green-500',
    iconBgClass: 'bg-gradient-to-br from-green-500 to-emerald-500',
    colSpanClass: 'md:col-span-2',
    testId: 'pricing-editor-btn',
    delay: 0.795,
  },
  {
    to: '/admin/podcasts',
    icon: Headphones,
    iconColor: 'text-purple-400',
    title: 'Podcast Management',
    description: 'Upload episodes, embed from platforms, view listen statistics',
    variant: 'wide',
    gradientClass: 'bg-gradient-to-br from-purple-500/30 to-pink-500/30',
    borderClass: 'border-2 border-purple-500/50 hover:border-purple-500',
    iconBgClass: 'bg-gradient-to-br from-purple-500 to-pink-500',
    colSpanClass: 'md:col-span-2',
    testId: 'podcasts-btn',
    delay: 0.79,
  },
  {
    to: '/admin/visual-editor',
    icon: Edit3,
    iconColor: 'text-amber-500',
    title: 'Visual Editor',
    description: 'Edit pages like Elementor - click to select',
    delay: 0.8,
  },
  {
    to: '/admin/page-builder',
    icon: FileText,
    iconColor: 'text-purple-500',
    title: 'New Page Builder',
    description: 'Create brand new pages with drag-drop blocks',
    delay: 0.85,
  },
  {
    to: '/admin/course-editor',
    icon: GraduationCap,
    iconColor: 'text-yellow-500',
    title: 'Course Editor',
    description: 'Edit all courses content & pricing',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-yellow-500/20 to-amber-500/20',
    borderClass: 'border-2 border-yellow-500/30 hover:border-yellow-500/60',
    testId: 'course-editor-btn',
    delay: 0.87,
  },
  {
    to: '/admin/pandeiro-curriculum',
    icon: GraduationCap,
    iconColor: 'text-amber-400',
    title: 'Pandeiro Curriculum',
    description: 'Edit Pandeiro Juggling chapters & lessons',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-amber-500/20 to-orange-500/20',
    borderClass: 'border-2 border-amber-500/30 hover:border-amber-500/60',
    testId: 'pandeiro-curriculum-btn',
    delay: 0.88,
  },
  {
    to: '/admin/class-editor',
    icon: Calendar,
    iconColor: 'text-green-500',
    title: 'Classes Editor',
    description: 'Edit all scheduled classes & private lessons',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-green-500/20 to-emerald-500/20',
    borderClass: 'border-2 border-green-500/30 hover:border-green-500/60',
    testId: 'class-editor-btn',
    delay: 0.89,
  },
  {
    to: '/admin/about-editor',
    icon: Crown,
    iconColor: 'text-yellow-500',
    title: 'Legacy / Mestre Page Editor',
    description: 'Edits the public /legacy page · Mestre Curtis story, education, funder pitch',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-yellow-500/20 to-amber-500/20',
    borderClass: 'border-2 border-yellow-500/30 hover:border-yellow-500/60',
    testId: 'about-editor-btn',
    delay: 0.92,
  },
  {
    to: '/admin/casa-samba-about-editor',
    icon: Users,
    iconColor: 'text-green-500',
    title: 'About Page Editor',
    description: 'Edits the public /about page · Hero, mission, history, leaders, members',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-green-500/20 to-emerald-500/20',
    borderClass: 'border-2 border-green-500/30 hover:border-green-500/60',
    testId: 'casa-samba-about-editor-btn',
    delay: 0.93,
  },
  {
    to: '/admin/ppv-editor',
    icon: Tv,
    iconColor: 'text-red-500',
    title: 'PPV Events Editor',
    description: 'Manage Pay-Per-View events & pricing',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-red-500/20 to-orange-500/20',
    borderClass: 'border-2 border-red-500/30 hover:border-red-500/60',
    testId: 'ppv-editor-btn',
    delay: 0.95,
  },
  {
    to: '/admin/play-along-editor',
    icon: Drum,
    iconColor: 'text-amber-500',
    title: 'Play Along Editor',
    description: 'Manage play-along practice videos',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-amber-500/20 to-orange-500/20',
    borderClass: 'border-2 border-amber-500/30 hover:border-amber-500/60',
    testId: 'play-along-editor-btn-secondary',
    delay: 0.97,
  },
  {
    to: '/admin/score-creator',
    icon: Music,
    iconColor: 'text-violet-500',
    title: 'Score Creator',
    description: 'Create orchestral arrangements for your percussion',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20',
    borderClass: 'border-2 border-violet-500/30 hover:border-violet-500/60',
    testId: 'score-creator-btn',
    delay: 0.975,
  },
  {
    to: '/admin/sample-manager',
    icon: Layers,
    iconColor: 'text-orange-500',
    title: 'Sample Manager',
    description: 'Upload & manage your instrument samples',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-orange-500/20 to-amber-500/20',
    borderClass: 'border-2 border-orange-500/30 hover:border-orange-500/60',
    testId: 'sample-manager-btn',
    delay: 0.977,
  },
  {
    to: '/admin/backing-tracks',
    icon: Music,
    iconColor: 'text-amber-400',
    title: 'Backing Tracks',
    description: 'Step 2 of Play Along — upload backing tracks & slow/med/fast tempo videos',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-amber-500/20 to-yellow-500/20',
    borderClass: 'border-2 border-amber-500/30 hover:border-amber-500/60',
    testId: 'backing-tracks-btn',
    delay: 0.978,
  },
  {
    to: '/admin/artwork-editor',
    icon: Palette,
    iconColor: 'text-purple-500',
    title: 'Artwork Editor',
    description: 'Manage your art gallery & sales',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-purple-500/20 to-pink-500/20',
    borderClass: 'border-2 border-purple-500/30 hover:border-purple-500/60',
    testId: 'artwork-editor-btn',
    delay: 0.98,
  },
  {
    to: '/admin/brazilian-grooves',
    icon: Drum,
    iconColor: 'text-amber-500',
    title: 'Brasilian Grooves',
    description: 'Manage percussion sample library',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-amber-500/20 to-orange-500/20',
    borderClass: 'border-2 border-amber-500/30 hover:border-amber-500/60',
    testId: 'brazilian-grooves-btn',
    delay: 0.985,
  },
  {
    to: '/admin/artwork-enhancer',
    icon: Wand2,
    iconColor: 'text-cyan-500',
    title: 'Artwork Enhancer',
    description: 'Enhance images for print sales',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20',
    borderClass: 'border-2 border-cyan-500/30 hover:border-cyan-500/60',
    testId: 'artwork-enhancer-btn',
    delay: 0.99,
  },
  {
    to: '/admin/page-builder',
    icon: Palette,
    iconColor: 'text-purple-500',
    title: 'Live Page Builder',
    description: 'Drag-and-drop visual page editor',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-purple-500/20 to-pink-500/20',
    borderClass: 'border-2 border-purple-500/30 hover:border-purple-500/60',
    testId: 'page-builder-btn',
    delay: 0.95,
  },
  {
    to: '/admin/recording-studio',
    icon: Radio,
    iconColor: 'text-red-500',
    title: 'Recording Studio',
    description: 'Manage client recording projects & async demos',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-red-500/20 to-pink-500/20',
    borderClass: 'border-2 border-red-500/30 hover:border-red-500/60',
    testId: 'recording-studio-btn',
    delay: 1.02,
  },
  {
    to: '/admin/remote-session',
    icon: Video,
    iconColor: 'text-blue-500',
    title: 'Live Remote Session',
    description: 'Real-time video collaboration with clients',
    variant: 'gradient',
    gradientClass: 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20',
    borderClass: 'border-2 border-blue-500/30 hover:border-blue-500/60',
    testId: 'remote-session-btn',
    delay: 1.05,
  },
];

// ---------- Analytics + QR Codes ----------
export const analyticsTiles = [
  {
    to: '/admin/analytics',
    icon: BarChart3,
    iconColor: 'text-cyan-500',
    title: 'Analytics',
    description: 'Sales reports, trends & insights',
    delay: 0.85,
  },
  {
    to: '/admin/qr-codes',
    icon: QrCode,
    iconColor: 'text-pink-500',
    title: 'QR Codes',
    description: 'Generate QR codes for products & classes',
    delay: 0.9,
  },
];
